import React from 'react';

// Context
import { useWorkspace } from 'context/WorkspaceContext';

// CSS
import './ApiHeader.css';

// Components
import Button from 'components/button/Button';

function toWholeNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed);
}

function formatCount(value) {
  return toWholeNumber(value).toLocaleString();
}

function formatQuotaValue(usedCount, limitCount) {
  const safeUsed = formatCount(usedCount);
  const safeLimit = toWholeNumber(limitCount);

  if (safeLimit < 1) {
    return `${safeUsed} used (uncapped)`;
  }

  return `${safeUsed} / ${safeLimit.toLocaleString()}`;
}

function formatRemainingCount(value, limitCount) {
  const safeLimit = toWholeNumber(limitCount);
  if (safeLimit < 1) return 'Unlimited';
  return formatCount(value);
}

/**
 * ApiHeader
 * 
 * This component renders the header section of the Workspace API page.
 * 
 * @param {boolean} canDownloadOpenApi - Flag to enable/disable the OpenAPI download button
 * @param {function} onDownloadOpenApi - Callback function to handle OpenAPI JSON download
 * @param {boolean} canDownloadSchema - Flag to enable/disable the download button
 * @param {function} onDownloadSchema - Callback function to handle schema download
 * 
 * @returns {JSX.Element} The rendered header card component
 */
const ApiHeader = ({
  apiLimits,
  canDownloadOpenApi,
  onDownloadOpenApi,
  canDownloadSchema,
  onDownloadSchema,
  theme,
}) => {

  // ----- CONTEXT ----- //

  // Workspace Context
  const { activeWorkspace } = useWorkspace();

  // ----- CONSTANTS ----- //

  const resolvedTitle = String(
    activeWorkspace?.title || 'Workspace',
  ).trim();
  const headerTitle = resolvedTitle ? `${resolvedTitle} - API` : 'Workspace API';
  const monthlyRuns = apiLimits?.usage?.apiMonthlyRuns || null;
  const monthlyRunLimit = toWholeNumber(monthlyRuns?.limit);
  const monthlyMonthKey = String(monthlyRuns?.monthKey || '').trim() || 'Current month';

  // ----- RENDER ----- //

  return (

    <div className="api-header-container">

      {/* DETAILS */}
      <div className="api-header-details">
        <h1
          className="api-header-title"
          style={{
            color: theme?.foreground,
          }}
        >
          {headerTitle}
        </h1>

        {monthlyRuns ? (
          <div
            className="api-header-usage-card"
            style={{
              backgroundColor: theme?.backgroundMuted,
              borderColor: theme?.backgroundTertiary,
              color: theme?.foreground,
            }}
          >
            <div className="api-header-usage-title">Monthly API Runs</div>
            <div
              className="api-header-usage-subtitle"
              style={{
                color: theme?.foregroundMuted,
              }}
            >
              {monthlyMonthKey}
            </div>

            <div className="api-header-usage-grid">
              <div className="api-header-usage-row">
                <span
                  className="api-header-usage-label"
                  style={{
                    color: theme?.foregroundMuted,
                  }}
                >
                  Runs used
                </span>
                <span>{formatQuotaValue(monthlyRuns?.requestCount, monthlyRunLimit)}</span>
              </div>

              <div className="api-header-usage-row">
                <span
                  className="api-header-usage-label"
                  style={{
                    color: theme?.foregroundMuted,
                  }}
                >
                  Remaining
                </span>
                <span>{formatRemainingCount(monthlyRuns?.remaining, monthlyRunLimit)}</span>
              </div>

              <div className="api-header-usage-row">
                <span
                  className="api-header-usage-label"
                  style={{
                    color: theme?.foregroundMuted,
                  }}
                >
                  Read requests
                </span>
                <span>{formatCount(monthlyRuns?.readCount)}</span>
              </div>

              <div className="api-header-usage-row">
                <span
                  className="api-header-usage-label"
                  style={{
                    color: theme?.foregroundMuted,
                  }}
                >
                  Write requests
                </span>
                <span>{formatCount(monthlyRuns?.writeCount)}</span>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* BUTTONS */}
      <div className="api-header-actions">

        {/* DOWNLOAD OPENAPI BUTTON */}
        <Button
          label="OpenAPI JSON"
          iconKey="download"
          onClick={onDownloadOpenApi}
          enabled={canDownloadOpenApi}
        />

        {/* DOWNLOAD SCHEMA BUTTON */}
        <Button
          label="Download Schema"
          iconKey="download"
          onClick={onDownloadSchema}
          enabled={canDownloadSchema}
        />

      </div>
    </div>
  );
};

export default ApiHeader;
